import { useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiButton,
  MiContentDrawer,
  MiDrawerHeader,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  APP_BAR_HEIGHT,
  APP_BAR_HEIGHT_TABLET,
  BUTTON_VARIANTS,
  MIBUILD_PAGE_HEADER_HEIGHT,
} from "@miplatform/constants";
import {
  ButtonVariants,
  IActionButtonConfig,
  IInspectionAttempt,
  IUploadedFile,
  InspectionActionTypes,
} from "@miplatform/types";
import {
  mdiAlertOutline,
  mdiCalendar,
  mdiCalendarOutline,
  mdiCalendarRefresh,
  mdiChevronDown,
  mdiClose,
  mdiOpenInNew,
  mdiTag,
} from "@mdi/js";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useInspectionsContext } from "../../InspectionsProvider";
import { GreenTagDrawer } from "./GreenTagDrawer/GreenTagDrawer";
import { InspectionDetails } from "./InspectionDetails/InspectionDetails";
import { InspectionDrawerHeader } from "./InspectionDrawerHeader/InspectionDrawerHeader";
import { RedTagDrawer } from "./RedTagDrawer/RedTagDrawer";
import { ScheduleDrawer } from "./ScheduleDrawer/ScheduleDrawer";
import {
  IInspectionForm,
  getInspectionFormDefaultValues,
} from "./inspectionFormSchemas";
import { useInspectionDrawerViewModel } from "./useInspectionDrawerViewModel";

export const InspectionDrawer = ({
  open,
  onClose,
  formControl,
}: {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  formControl: UseFormReturn<IInspectionForm>;
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const DRAWER_TOP_OFFSET =
    (isTablet ? APP_BAR_HEIGHT_TABLET : APP_BAR_HEIGHT) +
    MIBUILD_PAGE_HEADER_HEIGHT;

  const [inspectionConfig, setInspectionConfig] =
    useState<IInspectionConfig | null>(null);

  const {
    selectedInspectionAttempt,
    drawerType,
    setDrawerType,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
  } = useInspectionsContext();

  const { control, setValue, handleSubmit, reset, formState } =
    formControl ?? {};
  const { isDirty, isSubmitting, isValid } = formState ?? {};

  const {
    handleGreenTagSubmit,
    handleRedTagSubmit,
    handleScheduleSubmit,
    isLoading,
  } = useInspectionDrawerViewModel({
    onClose,
    setDrawerType,
  });

  const {
    approvedStartDate: scheduledDate,
    templateName,
    attemptNumber = 1,
  } = selectedInspectionAttempt || {};

  const onFileUploadChange = (fileList: IUploadedFile[]) => {
    const shouldDirty = fileList.length > 0;
    setValue("files", fileList, {
      shouldDirty,
      shouldValidate: true,
    });
  };

  const handleClose = () => {
    const requiresDiscardCheck = isDirty && !!drawerType;

    if (requiresDiscardCheck) {
      setIsConfirmationModalOpen(true);
    } else {
      onClose();
    }
  };

  const buildSubmitHandler = (
    inspectionAttemptData: IInspectionAttempt,
    options?: { scheduleNextInspection?: boolean }
  ) =>
    handleSubmit((values) => {
      switch (values.type) {
        case InspectionActionTypes.GreenTag: {
          handleGreenTagSubmit({
            id: inspectionAttemptData.id,
            ...values,
            files: values.files,
            externalReferenceId: inspectionAttemptData.referenceId,
          });
          break;
        }
        case InspectionActionTypes.RedTag: {
          handleRedTagSubmit({
            id: inspectionAttemptData.id,
            issues: values.issues,
            files: values.files,
            scheduleNextInspection: options?.scheduleNextInspection,
            assignmentId: inspectionAttemptData.assignmentId,
            externalReferenceId: inspectionAttemptData.referenceId,
          });
          break;
        }
        case InspectionActionTypes.Schedule:
        case InspectionActionTypes.Reschedule: {
          handleScheduleSubmit({
            attemptId: inspectionAttemptData.id,
            scheduledDate: values.scheduledDate,
          });
          break;
        }
        default:
          break;
      }
    });

  const getInspectionConfig = (
    type: InspectionActionTypes | null,
    inspectionAttemptData: IInspectionAttempt
  ): IInspectionConfig => {
    switch (type) {
      case InspectionActionTypes.GreenTag:
        return {
          title: `Green Tag: Enter Inspection Results for Attempt #${attemptNumber}`,
          positiveButtonConfig: [
            {
              name: "Submit Green Tag",
              color: "success",
              variant: ButtonVariants.Contained,
              startIcon: { path: mdiTag },
              onClick: () => buildSubmitHandler(inspectionAttemptData)(),
            },
          ],
          color: theme.palette.success.main,
          icon: mdiTag,
          renderContent: () => (
            <GreenTagDrawer
              control={control}
              onFileUploadChange={onFileUploadChange}
            />
          ),
        };
      case InspectionActionTypes.RedTag:
        return {
          title: `Red Tag: Enter Inspection Results for Attempt #${attemptNumber}`,
          positiveButtonConfig: [
            {
              name: "Submit without Schedule",
              color: "primary",
              variant: ButtonVariants.Outlined,
              onClick: () => buildSubmitHandler(inspectionAttemptData)(),
            },
            {
              name: "Submit Red Tag & Schedule",
              color: "error",
              variant: ButtonVariants.Contained,
              onClick: () => {
                buildSubmitHandler(inspectionAttemptData, {
                  scheduleNextInspection: true,
                })();
              },
            },
          ],
          color: theme.palette.error.main,
          icon: mdiTag,
          renderContent: () => (
            <RedTagDrawer
              control={control}
              onFileUploadChange={onFileUploadChange}
              setValue={setValue}
            />
          ),
        };
      case InspectionActionTypes.Reschedule: {
        return {
          title: "Reschedule Inspection",
          positiveButtonConfig: [
            {
              name: "Confirm Reschedule",
              color: "info",
              variant: ButtonVariants.Contained,
              startIcon: { path: mdiCalendarRefresh },
              onClick: () => {
                buildSubmitHandler(inspectionAttemptData)();
              },
            },
          ],
          color: theme.palette.info.main,
          icon: mdiCalendarRefresh,
          renderContent: () => (
            <ScheduleDrawer
              control={control}
              inspectionAttempt={inspectionAttemptData}
              title="Reschedule Inspection"
            />
          ),
        };
      }
      case InspectionActionTypes.Schedule: {
        const buttonConfig: IActionButtonConfig[] = [
          {
            name: "Schedule",
            color: "info",
            variant: ButtonVariants.Contained,
            onClick: () => {
              buildSubmitHandler(inspectionAttemptData)();
            },
          },
        ];
        if (inspectionAttemptData.home.municipalityWebsite) {
          buttonConfig.unshift({
            name: "Visit Municipalities Site",
            color: "info",
            variant: ButtonVariants.Outlined,
            endIcon: { path: mdiOpenInNew },
            onClick: () => {
              window.open(
                inspectionAttemptData.home.municipalityWebsite,
                "_blank"
              );
            },
          });
        }
        return {
          title: `Schedule ${templateName} for Attempt #${attemptNumber}`,
          positiveButtonConfig: buttonConfig,
          color:
            attemptNumber > 1
              ? theme.palette.warning.main
              : theme.palette.info.main,
          icon: attemptNumber > 1 ? mdiAlertOutline : mdiCalendar,
          renderContent: () => (
            <ScheduleDrawer
              control={control}
              inspectionAttempt={inspectionAttemptData}
            />
          ),
        };
      }
      default:
        return {
          title: "Inspection Details",
          color: theme.palette.info.main,
          icon: mdiTag,
          renderContent: () => <InspectionDetails />,
        };
    }
  };

  const { color, renderContent, positiveButtonConfig } = inspectionConfig || {};

  const { classes } = useStyles({ backgroundColor: color });

  useEffect(() => {
    let config: IInspectionConfig | null = null;
    if (selectedInspectionAttempt) {
      config = getInspectionConfig(
        drawerType ?? null,
        selectedInspectionAttempt
      );
    }
    reset(getInspectionFormDefaultValues({ scheduledDate, type: drawerType }));
    setInspectionConfig(config);
  }, [drawerType, selectedInspectionAttempt?.id]);

  const drawerActions = useMemo(() => {
    const actions = [];
    if (positiveButtonConfig) {
      actions.push(
        {
          name: "Cancel",
          onClick: () => {
            onClose();
          },
          disabled: isLoading || isSubmitting,
        },
        ...positiveButtonConfig.map((button) => ({
          ...button,
          disabled: isLoading || isSubmitting || !isValid,
        }))
      );
    }
    return actions;
  }, [
    drawerType,
    positiveButtonConfig,
    isValid,
    selectedInspectionAttempt?.approvedStartDate,
    selectedInspectionAttempt?.id,
    selectedInspectionAttempt?.attemptStatus,
    isLoading,
  ]);

  const actionButtonMenuItems = useMemo(() => {
    const items = [
      {
        label: "Red Tag",
        icon: mdiTag,

        color: theme.palette.error.main,
        action: () => {
          setDrawerType(InspectionActionTypes.RedTag);
        },
      },
      {
        label: "Green Tag",
        icon: mdiTag,

        color: theme.palette.success.main,
        action: () => {
          setDrawerType(InspectionActionTypes.GreenTag);
        },
      },
    ];
    if (selectedInspectionAttempt?.approvedStartDate) {
      items.unshift({
        label: "Reschedule",
        icon: mdiCalendarRefresh,
        color: theme.palette.info.main,
        action: () => {
          setDrawerType(InspectionActionTypes.Reschedule);
        },
      });
    } else {
      items.unshift({
        label: "Schedule",
        icon: mdiCalendarOutline,
        color: theme.palette.info.main,
        action: () => {
          setDrawerType(InspectionActionTypes.Schedule);
        },
      });
    }
    return items;
  }, [
    selectedInspectionAttempt?.approvedStartDate,
    selectedInspectionAttempt?.id,
    isLoading,
  ]);

  const { home } = selectedInspectionAttempt || {};
  const { address, communityName = "" } = home || {};
  const { addressLine1 = "", city = "", regionCode = "" } = address || {};

  return (
    <MiContentDrawer
      open={open}
      onClose={handleClose}
      isLoading={isLoading || isSubmitting}
      actions={drawerActions}
      hideBackdrop={false}
      variant="persistent"
      sx={{
        "& > .MuiPaper-root": {
          top: DRAWER_TOP_OFFSET,
          height: `calc(100% - ${DRAWER_TOP_OFFSET}px)`,
        },
      }}
      Header={
        <MiDrawerHeader
          title={addressLine1}
          subtitle={[`${city}, ${regionCode}`, communityName]}
          rightAction={
            <MiActions
              type="ICONS"
              actions={[
                {
                  name: "Close",
                  icon: mdiClose,
                  onClick: handleClose,
                },
              ]}
            />
          }
        />
      }
    >
      <Box
        component="form"
        className={classes.drawerContent}
        onSubmit={
          selectedInspectionAttempt && drawerType
            ? buildSubmitHandler(selectedInspectionAttempt)
            : undefined
        }
      >
        <Box className={classes.drawerTitleActions}>
          <Typography variant="HD3">
            {selectedInspectionAttempt?.templateName}
          </Typography>
          {!drawerType && (
            <Box>
              <MiMenu
                data={actionButtonMenuItems}
                optionalElement={
                  <MiButton
                    title="Actions"
                    variant={BUTTON_VARIANTS.CONTAINED}
                    endIcon={{ path: mdiChevronDown }}
                  />
                }
              />
            </Box>
          )}
        </Box>
        <InspectionDrawerHeader
          status={selectedInspectionAttempt?.attemptStatus}
          scheduledDate={selectedInspectionAttempt?.approvedStartDate}
          attemptNumber={attemptNumber}
          reportedDate={selectedInspectionAttempt?.reportedDate}
          inspectedDate={selectedInspectionAttempt?.inspectedDate}
        />
        {renderContent?.()}
      </Box>
      {isConfirmationModalOpen && (
        <Box>
          <MiModalConfirm
            open={isConfirmationModalOpen}
            textPositive="Continue"
            textNegative="No, stay"
            handleNegative={() => setIsConfirmationModalOpen(false)}
            handlePositive={() => onClose()}
            title="Discard changes"
            description="You are about to discard unsaved changes. Are you sure you want to continue?"
          />
        </Box>
      )}
    </MiContentDrawer>
  );
};

const useStyles = makeStyles<{ backgroundColor?: string }>()(
  (theme, { backgroundColor }) => ({
    drawerHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingLeft: theme.spacing(3),
      color: theme.palette.common.white,
      backgroundColor: backgroundColor || theme.palette.primary.main,
    },
    drawerTitleActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(4),
    },
    drawerContent: {
      width: "100%",
      padding: theme.spacing(4),
    },
  })
);

interface IInspectionConfig {
  title: string;
  color: string;
  icon: string;
  renderContent: () => React.ReactNode;
  positiveButtonConfig?: IActionButtonConfig[];
}
